import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { UploadWizard } from '~/components/upload/UploadWizard'
import {
  MAX_CSV_FILE_SIZE_BYTES,
  MAX_CSV_ROW_COUNT,
} from '~/constants/csvUpload'
import { useCsvImportReturn } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { downloadCsv } from '~/libs/downloadCsv'
import {
  DEMAND_GROUP_CSV_HEADER,
  DEMAND_GROUP_CSV_HEADER_EN,
} from '~/models/demandGroupCsvHeader'
import { useApplyDemandGroupCsv } from '~/pages/DemandGroupCsvUpload/hooks/useApplyDemandGroupCsv'
import { useCheckDemandGroupCsv } from '~/pages/DemandGroupCsvUpload/hooks/useCheckDemandGroupCsv'
import {
  DEMAND_GROUP_CSV_TEMPLATE_ROWS,
  buildDemandGroupCsvTemplate,
  demandGroupCsvTemplateFileName,
} from '~/pages/DemandGroupCsvUpload/models/demandGroupCsvTemplate'
import { routePaths } from '~/routes/routePaths'

type CheckResult = {
  checkToken: string
  groupCount: number
  supplyPointCount: number
}

type ApplyResult = {
  createdGroupCount: number
  createdSupplyPointCount: number
}

/**
 * 需要グループ CSV アップロード画面 (`/admin/uploads/demand-groups`,
 * demand-groups-upload-tracer AC-01〜AC-10)。
 *
 * 配置は design `admin/uploads/demand-groups.html` の独立ルート + 3 階層
 * パンくず (システム管理 > CSVアップロード > 需要グループ) に合わせている。
 * design は 2026-07-16 に `/admin/database` の「データ取込」タブを撤去して
 * CSVアップロードを独立カテゴリに分離しており、先行実装の
 * `Supply48DataCsvUpload` も同じ形。 CSVアップロード一覧
 * (`admin/uploads/index.md`) の本格実装は別 spec のスコープなので、本画面は
 * 一覧を経由せず直接開ける単独ルートとして置く。
 */
export const DemandGroupCsvUploadPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 英語表示では雛形のファイル名・ヘッダー行と、期待ヘッダーの表示を英語にする (common.md §6)。
  const isEnglish = i18n.language.startsWith('en')
  const showSuccessToast = useSuccessToast()
  const csvReturn = useCsvImportReturn('demandGroups')
  const checkMutation = useCheckDemandGroupCsv()
  const applyMutation = useApplyDemandGroupCsv()

  const handleCheck = async (file: File): Promise<CheckResult> => {
    const csvContent = new Uint8Array(await file.arrayBuffer())
    const res = await checkMutation.mutateAsync({ csvContent })
    return {
      checkToken: res.checkToken,
      groupCount: res.groupCount,
      supplyPointCount: res.supplyPointCount,
    }
  }

  const handleApply = async (checkToken: string): Promise<ApplyResult> => {
    const res = await applyMutation.mutateAsync({ checkToken })
    return {
      createdGroupCount: res.createdGroupCount,
      createdSupplyPointCount: res.createdSupplyPointCount,
    }
  }

  // Step2 の件数サマリと確定モーダル本文は同じ値・同じ文言 (Design Note
  // upload-wizard-two-step.md D10) なので 1 つの描画を両方に渡す。
  const renderCounts = (result: CheckResult): React.ReactElement => (
    <Text>
      {t('page:demandGroupCsvUpload.summary', {
        groupCount: result.groupCount,
        supplyPointCount: result.supplyPointCount,
      })}
    </Text>
  )

  /** Step1: ヘッダー行 + 記入例3行 (common.md AC-DUC-031、spec §1「雛形の記入例」) の雛形を DL する。 */
  const handleTemplateDownload = (): void => {
    downloadCsv(
      demandGroupCsvTemplateFileName(isEnglish),
      buildDemandGroupCsvTemplate(isEnglish),
    )
  }

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:demandGroupCsvUpload.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminUploads'),
          to: routePaths.adminUploads.path,
        },
        { label: t('page:demandGroupCsvUpload.breadcrumb.title'), bold: true },
      ]}
    >
      {/* spec `admin/data/uploads/index.md:126` :
          「個別アップロード画面のタイトル下補足も本表の説明文と一致させる。 文言変更は
           本 spec の更新をもって行い、 **実装・プロトタイプ側で独自の説明文を持たない**」。
          そのため取込ハブのカードと**同じ i18n キーを共有**する (別キーに複製すると
          片方だけ直って drift する = issue #4325 で禁じられた状態に戻る)。
          見出しは渡さない — パンくず末尾 (bold) が画面名を兼ねる (PageIntro の docstring)。 */}
      <PageIntro description={t('page:adminUploads.types.demandGroups.desc')} />
      <PageBody>
        <UploadWizard<CheckResult, ApplyResult>
          // AC-DUG-001 / AC-SPS-031: 日本語・英語どちらのヘッダーも受理する (英語は大文字・小文字を区別しない)。
          acceptedHeaders={{
            ja: [DEMAND_GROUP_CSV_HEADER],
            en: [DEMAND_GROUP_CSV_HEADER_EN],
          }}
          // 確定モーダル本文は件数のみ。 design (`demand-groups.html` 確定モーダル)
          // の案内文「バックグラウンドで開始…完了を待たずに別画面に移れます」は
          // 非同期実行 (P2) 前提で、 同期実行の現状と矛盾するため使わない
          // (Design Note upload-wizard-two-step.md D10)。 件数は Check レスポンスに
          // ある値だけ — design の区分別件数行 (新規 / 変更 / 論理削除 / 変更なし)
          // は差分判定 (map #2946 slice 2) まで RPC が返さない。
          confirmDialog={{
            openLabel: t('page:demandGroupCsvUpload.confirmModal.open'),
            title: t('page:demandGroupCsvUpload.confirmModal.title'),
            confirmLabel: t('page:demandGroupCsvUpload.confirmModal.commit'),
            renderBody: renderCounts,
          }}
          displayHeader={isEnglish ? DEMAND_GROUP_CSV_HEADER_EN : undefined}
          downloads={[{ onClick: handleTemplateDownload }]}
          expectedHeader={DEMAND_GROUP_CSV_HEADER}
          fileSelectionDisabled={csvReturn.isReturning}
          maxFileSizeBytes={MAX_CSV_FILE_SIZE_BYTES}
          maxRowCount={MAX_CSV_ROW_COUNT}
          onApply={handleApply}
          onCheck={handleCheck}
          onComplete={(result) => {
            showSuccessToast({
              title: t('page:demandGroupCsvUpload.completeToast.title'),
              description: t(
                'page:demandGroupCsvUpload.completeToast.description',
                {
                  groupCount: result.createdGroupCount,
                  supplyPointCount: result.createdSupplyPointCount,
                },
              ),
            })
            // 件数は新規・変更・削除の形で返らないので渡さない
            csvReturn.returnAfterApply()
          }}
          renderSummary={renderCounts}
          // AC-DUC-035 (D6): 代表列は主キー先頭の「需要BGコード」、代表値は
          // 雛形記入例 1 行目のその値。
          sampleRow={{
            column: '需要BGコード',
            example: DEMAND_GROUP_CSV_TEMPLATE_ROWS[0].demandBgCode,
          }}
          selectHint={t('page:demandGroupCsvUpload.step1.hint')}
        />
      </PageBody>
    </PageLayout>
  )
}
